import { Box, Button, Typography } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { AddCustomListToOrderConfirmDialog } from 'features/AddCustomListToOrder';
import { Loader } from 'shared/ui/Loader/Loader';

import { useOrderAddItemsLists } from './useOrderAddItemsLists';

import { OrderAddItemsListsPanel } from '../OrderAddItemsListsPanel/OrderAddItemsListsPanel';
import { OrderAddItemsListsRail } from '../OrderAddItemsListsRail/OrderAddItemsListsRail';

export const OrderAddItemsLists = memo(() => {
    const { t } = useTranslation();
    const {
        lists,
        isListsLoading,
        listsError,
        listId,
        onSelectList,
        openConfirmForList,
        activeList,
        panelItems,
        itemSearch,
        categoryFilter,
        isPanelLoading,
        detailFailed,
        onRetryLists,
        onRetryDetail,
        confirmList,
        confirmMode,
        selectedOrderId,
        closeConfirm,
        onConfirmSuccess,
    } = useOrderAddItemsLists();

    if (isListsLoading && lists.length === 0) {
        return (
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Loader />
            </Box>
        );
    }

    if (!isListsLoading && listsError && lists.length === 0) {
        return (
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '24px',
                }}
                data-testclass="order-add-items-lists-error"
            >
                <Typography variant="body1" color="text.secondary">
                    {t('customLists.failedToLoadLists')}
                </Typography>
                <Button variant="outlined" onClick={onRetryLists}>
                    {t('customLists.tryAgain')}
                </Button>
            </Box>
        );
    }

    if (!isListsLoading && lists.length === 0) {
        return (
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '24px',
                }}
                data-testclass="order-add-items-lists-empty"
            >
                <Typography variant="h4">
                    {t('customLists.noListsTitle')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t('customLists.noListsDescription')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
            data-testclass="order-add-items-lists"
        >
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    minHeight: 0,
                    overflow: 'hidden',
                    backgroundColor: 'rgba(233, 232, 232, 0.30)',
                    borderRadius: '12px 12px 0 0',
                    padding: '16px',
                    gap: '12px',
                }}
            >
                <OrderAddItemsListsRail
                    lists={lists}
                    activeListId={listId}
                    onSelectList={onSelectList}
                    onAddAll={openConfirmForList}
                />
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                    }}
                >
                    <OrderAddItemsListsPanel
                        list={activeList}
                        items={panelItems}
                        itemSearch={itemSearch}
                        categoryFilter={categoryFilter}
                        isLoading={isPanelLoading}
                        hasError={detailFailed}
                        onRetry={onRetryDetail}
                    />
                </Box>
            </Box>

            {confirmList ? (
                <AddCustomListToOrderConfirmDialog
                    open
                    listId={confirmList.list.id}
                    listName={confirmList.list.name}
                    listItems={confirmList.items}
                    mode={confirmMode}
                    orderId={selectedOrderId}
                    onClose={closeConfirm}
                    onSuccess={onConfirmSuccess}
                />
            ) : null}
        </Box>
    );
});

OrderAddItemsLists.displayName = 'OrderAddItemsLists';
